/* eslint-disable import/no-unresolved */
import { useMemo, useState, useRef, useEffect } from 'react';
import 'swiper/css';
import 'swiper/css/pagination';
import { cx } from '@emotion/css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Skeleton } from '@mui/material';
import SwiperCore, { Pagination } from 'swiper';
import colors from '@/theme/colors';
import { FlyerPage } from '@/types/flyer';
import { Divider, LiveFeedback } from '@/components';
import styles from './FlyerDetailsSwiper.styles';
import { BodySmallEmphasized } from '@/design-system';
import InteractiveFlyer from '../InteractiveFlyer/InteractiveFlyer';
import { CallToAction, Flyer } from '@/graphql/types';
import config from '@/config/config';
import { useLiveFeedback } from '@/hooks';

SwiperCore.use([Pagination]);

interface Props {
  flyer: Flyer;
  loadingFlyer: boolean;
  isNonActive: boolean;
  audienceId?: string | null;
  onLoadFlyer: () => void;
  slideSwiper: () => void;
}

export const FlyerDetailsSwiper: React.FC<Props> = ({
  flyer,
  loadingFlyer,
  isNonActive,
  onLoadFlyer,
  slideSwiper,
}) => {
  const { pages, flyerId, districtId, ctas, title: flyerTitle, email, pageCount } = flyer;
  const featuredLinkIds = flyer.featuredLinkIds ?? [];
  const [activeSlide, setActiveSlide] = useState<number>(1);
  const [highlightedLink, highlightLink] = useState<CallToAction['id']>();
  const [feedbackMessage, setFeedbackMessage] = useLiveFeedback({ delay: 2000 });
  const slidesPerView = useMemo(() => (pages.length > 1 ? 1.08 : 1), [pages]);
  const spaceBetween = useMemo(() => (slidesPerView === 1 ? 0 : 10), [slidesPerView]);

  const swiperRef = useRef<SwiperCore | null>(null);

  const handleSlideChange = (core: SwiperCore) => {
    const newActiveIndex = core.activeIndex + 1;
    setActiveSlide(newActiveIndex);
    slideSwiper();
    setFeedbackMessage(`Showing slide ${newActiveIndex} of ${pages.length}`);
  };

  const handlePaginationClick = (index: number) => {
    swiperRef.current?.slideTo(index);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === 'ArrowLeft') {
      swiperRef.current?.slideTo(index - 1);
    } else if (event.key === 'ArrowRight') {
      swiperRef.current?.slideTo(index + 1);
    }
  };

  const renderCustomPagination = () => {
    if (pages.length <= 1) return null;
    return (
      <ul className={styles.customPagination}>
        {pages.map((_, index) => (
          <li key={index}>
            <button
              type="button"
              role="button"
              tabIndex={0}
              className={cx(styles.paginationButton, {
                [styles.paginationButtonActive]: index + 1 === activeSlide,
              })}
              aria-label={`Slide ${index + 1}`}
              aria-current={index + 1 === activeSlide ? 'true' : undefined}
              onClick={() => handlePaginationClick(index)}
              onKeyDown={(event) => handleKeyDown(event, index)}
            >
              <span className="sr-only">{`Slide ${index + 1}`}</span>
            </button>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div
      aria-roledescription="carousel"
      aria-label={`Flyer carousel: ${flyerTitle}`}
      aria-labelledby="flyer-carousel-title"
    >
      {loadingFlyer && featuredLinkIds.length === 0 && (
        <Skeleton variant="rectangular" width="100%" height={350} />
      )}
      <LiveFeedback message={feedbackMessage} />
      <Divider />
      <Swiper
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        simulateTouch={true}
        slidesPerView={slidesPerView}
        spaceBetween={spaceBetween}
        centeredSlides={false}
        modules={[Pagination]}
        className={styles.swiper}
        onSlideChange={handleSlideChange}
        data-testid="slides"
        pagination={{
          el: '.custom-pagination',
          clickable: true,
        }}
        role="list"
      >
        {pages.map((page, index) => {
          const ariaDescribedby = `flyer-Description-Details-${page.pageNumber}`;
          const slideLabel = `slide-${index + 1}-label`;
          const slideKey = `slide-${page.pageNumber}`;
          return (
            <li key={slideKey} role="listitem">
              <SwiperSlide
                id={`slide-${index + 1}`}
                aria-roledescription="slide"
                aria-labelledby={slideLabel}
                data-testid="slideItem"
                key={slideKey}
              >
                <>
                  <span id={slideLabel} className="sr-only">
                    Slide {index + 1} of {pages.length}
                  </span>
                  {featuredLinkIds.length > 0 ? (
                    <InteractiveFlyer
                      key={`page${activeSlide}`}
                      flyerId={flyerId}
                      districtId={districtId}
                      pageNumber={activeSlide}
                      flyerImage={page.imageUrl}
                      highlightedLink={highlightedLink}
                      highlightLink={highlightLink}
                      isNonActive={isNonActive}
                      ctas={ctas}
                      featuredLinkIds={featuredLinkIds}
                      onLoadFlyer={onLoadFlyer}
                      loadingFlyer={loadingFlyer}
                      aria-describedby={ariaDescribedby}
                    />
                  ) : (
                    <img
                      src={`https://flyers-bff.${config.PEACHJAR_DOMAIN}/view/district/${districtId}/flyer/${flyerId}/${page.pageNumber}.jpg`}
                      className={cx(
                        styles.flyer,
                        loadingFlyer && styles.hide,
                        isNonActive && styles.flyerOpacity
                      )}
                      alt={`Image of a flyer titled ${flyerTitle}`}
                      aria-describedby={ariaDescribedby}
                      loading="lazy"
                      onLoad={onLoadFlyer}
                    />
                  )}
                  <span id={ariaDescribedby} className={styles.flyerDescription}>
                    Text Found On Flyer - {page.ocrText || 'No Text Available'} Need more
                    information on this flyer? You can email the person who submitted the
                    flyer at {email} to learn more.
                  </span>
                </>
              </SwiperSlide>
            </li>
          );
        })}
        {!loadingFlyer && pageCount > 1 && (
          <div className={styles.swiperPagination}>
            <BodySmallEmphasized color={colors.white} data-testid="pageNumber">
              {`${activeSlide}/${pageCount}`}
            </BodySmallEmphasized>
          </div>
        )}
      </Swiper>
      {renderCustomPagination()}
    </div>
  );
};
